import { useCallback, useEffect, useRef, type RefCallback } from 'react';

/**
 * D65: a row that scrolls sideways (chips, a FilterBar on a phone, tabs) says there is
 * more. While part of it is hidden, the element gets `data-fade` = `end`, `start` or
 * `both`, and the CSS fades that edge (a `mask-image`), so the last visible chip
 * looks cut on purpose. The chosen item is scrolled into view (with `scroll-padding`).
 */
export function useScrollFade<T extends HTMLElement>(): RefCallback<T> {
  const cleanup = useRef<() => void>();
  useEffect(() => () => cleanup.current?.(), []);
  return useCallback((el: T | null) => {
    cleanup.current?.();
    cleanup.current = undefined;
    if (!el) return;
    const update = () => {
      const max = el.scrollWidth - el.clientWidth;
      // One pixel of slack for sub-pixel widths.
      const start = el.scrollLeft > 1;
      const end = max > 1 && el.scrollLeft < max - 1;
      const fade = start && end ? 'both' : start ? 'start' : end ? 'end' : undefined;
      if (fade) el.dataset.fade = fade;
      else delete el.dataset.fade;
    };
    update();
    // The pressed or selected item, scrolled into the row without moving the page.
    const chosen = el.querySelector<HTMLElement>('[aria-pressed="true"], [aria-selected="true"]');
    if (chosen && el.scrollWidth > el.clientWidth) {
      const left =
        chosen.getBoundingClientRect().left - el.getBoundingClientRect().left + el.scrollLeft;
      if (left + chosen.offsetWidth > el.clientWidth) el.scrollLeft = left;
      update();
    }
    el.addEventListener('scroll', update, { passive: true });
    const resize = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(update);
    resize?.observe(el);
    // Counts and chips that arrive later change the width of the content, not of the row.
    const changes =
      typeof MutationObserver === 'undefined' ? undefined : new MutationObserver(update);
    changes?.observe(el, { childList: true, subtree: true, characterData: true });
    cleanup.current = () => {
      el.removeEventListener('scroll', update);
      resize?.disconnect();
      changes?.disconnect();
    };
  }, []);
}
